<div class="wizard-shell">
  <mat-card class="wizard-header" [style.background-color]="uiData.firm.color">
    <mat-card-title>
      <mat-icon>{{ uiData.firm.icon }}</mat-icon>
      {{ dialogTitle }}
    </mat-card-title>
  </mat-card>

  <div *ngIf="loading" class="spinner-container">
    <mat-spinner diameter="60" strokeWidth="6"></mat-spinner>
  </div>

  <ng-container *ngIf="!loading && application">
    <mat-card class="status-summary-card" *ngIf="showApplicationStatusSummary">
      <div class="status-summary-grid">
        <div class="status-summary-item">
          <div class="status-summary-label">Application Status</div>
          <div class="status-summary-pill" [ngStyle]="{ '--status-color': sharedService.getStatusColor(displayApplicationStatus) }">
            {{ displayApplicationStatus }}
          </div>
        </div>

        <div class="status-summary-item" *ngIf="applicationStatusDate">
          <div class="status-summary-label">{{ applicationStatusDateLabel }}</div>
          <div class="status-summary-value">{{ applicationStatusDate | date:'MM/dd/yyyy' }}</div>
        </div>
      </div>
    </mat-card>

    <div class="wizard-body">
      <div class="wizard-content">
        <ng-container *ngIf="showWorkflowChooser; else workflowShell">
          <div class="scroll-container chooser-scroll">
            <div class="step-content" fxLayout="column" fxLayoutGap="16px">
              <mat-card class="info-banner">
                <div fxLayout="row" fxLayoutAlign="start start" fxLayoutGap="12px">
                  <mat-icon>info</mat-icon>
                  <span>Select how you want to start your Firm application. You can either begin a new application or request to link an existing Firm.</span>
                </div>
              </mat-card>

              <div class="chooser-grid" fxLayout="row" fxLayout.lt-md="column" fxLayoutGap="16px" fxLayoutAlign="start stretch">
                <div fxFlex="50" fxFlex.lt-md="100">
                  <mat-card class="chooser-card">
                    <div class="chooser-card-body" fxLayout="column" fxLayoutGap="12px">
                      <div class="chooser-card-title">Apply for a New Firm</div>
                      <p>Start a new public Firm application for LBPA or RRP services.</p>
                      <div fxFlex="1 1 auto"></div>
                      <div fxLayout="row" fxLayoutAlign="center">
                        <button mat-raised-button type="button" color="primary" (click)="selectNewApplication()">
                          <mat-icon>add</mat-icon>
                          New Application
                        </button>
                      </div>
                    </div>
                  </mat-card>
                </div>

                <div fxFlex="50" fxFlex.lt-md="100">
                  <mat-card class="chooser-card">
                    <div class="chooser-card-body" fxLayout="column" fxLayoutGap="12px">
                      <div class="chooser-card-title">Link to an Existing Firm</div>
                      <p>Request access to an already existing public Firm through the same application workflow.</p>
                      <div fxFlex="1 1 auto"></div>
                      <div fxLayout="row" fxLayoutAlign="center">
                        <button mat-raised-button type="button" color="primary" (click)="selectLinkApplication()">
                          <mat-icon>link</mat-icon>
                          Link Existing Firm
                        </button>
                      </div>
                    </div>
                  </mat-card>
                </div>
              </div>
            </div>
          </div>
        </ng-container>

        <ng-template #workflowShell>
        <mat-stepper #firmStepper [selectedIndex]="currentStep" (selectionChange)="onStepSelectionChange($event)" [orientation]="sharedService.isMobile ? 'vertical' : 'horizontal'" class="firm-stepper">
          <ng-container *ngIf="!isLinkWorkflow; else linkWorkflow">
            <mat-step [hasError]="stepHasValidationErrors(0)">
              <ng-template matStepLabel>Application Information</ng-template>

              <div class="scroll-container" [class.has-feedback]="hasFeedbackContent()">
                <form [formGroup]="applicationForm" class="step-content" fxLayout="column" fxLayoutGap="16px">
                  <mat-card class="info-banner">
                    <div fxLayout="row" fxLayoutAlign="start start" fxLayoutGap="12px">
                      <mat-icon>info</mat-icon>
                      <span *ngIf="isRenewalApplication">
                        This renewal starts from an affiliated Firm workspace. The application type and renewal target are preselected.
                      </span>
                      <span *ngIf="!isRenewalApplication">
                        Select the application type first. You can save a draft from any step before final submission.
                      </span>
                    </div>
                  </mat-card>

                  <mat-card class="content-card workflow-card">
                    <mat-card-title>Application Information</mat-card-title>
                    <mat-card-content>
                      <div fxLayout="column" fxLayoutGap="12px">
                        <mat-radio-group class="type-selector" [disabled]="!canEdit || isRenewalApplication" formControlName="applicationType">
                          <mat-card class="type-card" [class.selected]="applicationForm.get('applicationType')?.value === FIRM_APPLICATION_TYPES.lbpa">
                            <mat-radio-button [value]="FIRM_APPLICATION_TYPES.lbpa">Lead-Based Paint Activities</mat-radio-button>
                          </mat-card>

                          <mat-card class="type-card" [class.selected]="applicationForm.get('applicationType')?.value === FIRM_APPLICATION_TYPES.rrp">
                            <mat-radio-button [value]="FIRM_APPLICATION_TYPES.rrp">Renovation, Repair and Painting</mat-radio-button>
                          </mat-card>
                        </mat-radio-group>
                        <div class="field-error-text" *ngIf="showApplicationControlError('applicationType', 'required')">Application type is required.</div>
                      </div>
                    </mat-card-content>
                  </mat-card>
                </form>
              </div>
            </mat-step>

            <mat-step [hasError]="stepHasValidationErrors(1)">
              <ng-template matStepLabel>Firm Information</ng-template>

              <div class="scroll-container" [class.has-feedback]="hasFeedbackContent()">
                <form [formGroup]="applicationForm" class="step-content" fxLayout="column" fxLayoutGap="16px">
                  <mat-card class="info-banner">
                    <div fxLayout="row" fxLayoutAlign="start start" fxLayoutGap="12px">
                      <mat-icon>info</mat-icon>
                      <span>Complete the Firm details, address, contact information, and contact person before continuing.</span>
                    </div>
                  </mat-card>

                  <div class="card-pair-grid">
                    <mat-card class="content-card workflow-card">
                      <mat-card-title>Firm Details</mat-card-title>
                      <mat-card-content>
                        <div fxLayout="column" fxLayoutGap="12px">
                          <mat-form-field>
                            <mat-label>Firm Name</mat-label>
                            <input matInput formControlName="firmName">
                            <mat-error *ngIf="showApplicationControlError('firmName', 'required')">Firm name is required.</mat-error>
                          </mat-form-field>

                          <mat-form-field>
                            <mat-label>{{ applicationForm.get('applicationType')?.value === FIRM_APPLICATION_TYPES.lbpa ? 'CCB License #' : 'Property Management License #' }}</mat-label>
                            <input matInput formControlName="ccb">
                          </mat-form-field>

                          <mat-form-field *ngIf="showLbpaFields">
                            <mat-label>My Firm Provides Services For</mat-label>
                            <mat-select formControlName="firmServices">
                              <mat-option *ngFor="let type of serviceForTypes" [value]="type">{{ type }}</mat-option>
                            </mat-select>
                          </mat-form-field>

                          <mat-checkbox *ngIf="showLbpaFields" formControlName="isPublic">Private Firm</mat-checkbox>

                          <mat-form-field *ngIf="showLbpaFields">
                            <mat-label>Firm Coverage</mat-label>
                            <textarea matInput formControlName="coverage" rows="3" placeholder="Describe where your firm offers services. If your firm is public, this text will be shown exactly as entered."></textarea>
                            <mat-hint>Describe where your firm offers services. If your firm is public, this text will be shown exactly as entered.</mat-hint>
                          </mat-form-field>
                        </div>
                      </mat-card-content>
                    </mat-card>

                    <mat-card class="content-card workflow-card">
                      <mat-card-title>Firm Address</mat-card-title>
                      <mat-card-content>
                        <div fxLayout="column" fxLayoutGap="12px">
                          <div class="address-block">
                            <div class="address-section-title">Physical Address</div>

                            <div class="address-grid-primary">
                              <mat-form-field>
                                <mat-label>Physical Street</mat-label>
                                <input matInput formControlName="firmPhysicalStreet">
                                <mat-error *ngIf="showApplicationControlError('firmPhysicalStreet', 'required')">Street address is required.</mat-error>
                              </mat-form-field>

                              <mat-form-field>
                                <mat-label>City</mat-label>
                                <input matInput formControlName="firmPhysicalCity">
                                <mat-error *ngIf="showApplicationControlError('firmPhysicalCity', 'required')">City is required.</mat-error>
                              </mat-form-field>
                            </div>

                            <div class="address-grid-secondary" [class.with-county]="showPhysicalCounty">
                              <mat-form-field>
                                <mat-label>State</mat-label>
                                <mat-select formControlName="firmPhysicalState" (selectionChange)="onStateSelectionChange('firmPhysicalState')">
                                  <mat-option *ngFor="let state of sharedService.states" [value]="state">{{ state }}</mat-option>
                                </mat-select>
                                <mat-error *ngIf="showApplicationControlError('firmPhysicalState', 'required')">State is required.</mat-error>
                              </mat-form-field>

                              <mat-form-field>
                                <mat-label>ZIP</mat-label>
                                <input matInput formControlName="firmPhysicalZip">
                                <mat-error *ngIf="showApplicationControlError('firmPhysicalZip', 'required')">ZIP code is required.</mat-error>
                                <mat-error *ngIf="showApplicationControlError('firmPhysicalZip', 'pattern')">Enter a valid ZIP code.</mat-error>
                              </mat-form-field>

                              <mat-form-field *ngIf="showPhysicalCounty">
                                <mat-label>County</mat-label>
                                <mat-select formControlName="firmPhysicalCounty">
                                  <mat-option [value]="''">Select County</mat-option>
                                  <mat-option *ngFor="let county of sharedService.counties" [value]="county">{{ county }}</mat-option>
                                </mat-select>
                                <mat-error *ngIf="showCountyRequiredError('firmPhysicalCounty')">County is required when the state is Oregon.</mat-error>
                              </mat-form-field>
                            </div>
                          </div>

                          <mat-checkbox formControlName="mailingIsPhysical">Mailing address is the same as physical</mat-checkbox>

                          <div class="address-block" *ngIf="showMailingAddress">
                            <div class="address-section-title">Mailing Address</div>

                            <div class="address-grid-primary">
                              <mat-form-field>
                                <mat-label>Mailing Street</mat-label>
                                <input matInput formControlName="firmMailingStreet">
                                <mat-error *ngIf="showMailingRequiredError('firmMailingStreet')">Mailing street is required.</mat-error>
                              </mat-form-field>

                              <mat-form-field>
                                <mat-label>City</mat-label>
                                <input matInput formControlName="firmMailingCity">
                                <mat-error *ngIf="showMailingRequiredError('firmMailingCity')">Mailing city is required.</mat-error>
                              </mat-form-field>
                            </div>

                            <div class="address-grid-secondary" [class.with-county]="showMailingCounty">
                              <mat-form-field>
                                <mat-label>State</mat-label>
                                <mat-select formControlName="firmMailingState" (selectionChange)="onStateSelectionChange('firmMailingState')">
                                  <mat-option *ngFor="let state of sharedService.states" [value]="state">{{ state }}</mat-option>
                                </mat-select>
                                <mat-error *ngIf="showMailingRequiredError('firmMailingState')">Mailing state is required.</mat-error>
                              </mat-form-field>

                              <mat-form-field>
                                <mat-label>ZIP</mat-label>
                                <input matInput formControlName="firmMailingZip">
                                <mat-error *ngIf="showMailingRequiredError('firmMailingZip')">Mailing ZIP code is required.</mat-error>
                                <mat-error *ngIf="showApplicationControlError('firmMailingZip', 'pattern')">Enter a valid ZIP code.</mat-error>
                              </mat-form-field>

                              <mat-form-field *ngIf="showMailingCounty">
                                <mat-label>County</mat-label>
                                <mat-select formControlName="firmMailingCounty">
                                  <mat-option [value]="''">Select County</mat-option>
                                  <mat-option *ngFor="let county of sharedService.counties" [value]="county">{{ county }}</mat-option>
                                </mat-select>
                                <mat-error *ngIf="showCountyRequiredError('firmMailingCounty')">County is required when the state is Oregon.</mat-error>
                              </mat-form-field>
                            </div>
                          </div>
                        </div>
                      </mat-card-content>
                    </mat-card>
                  </div>

                  <div class="card-pair-grid">
                    <mat-card class="content-card workflow-card">
                      <mat-card-title>Firm Contact Information</mat-card-title>
                      <mat-card-content>
                        <div fxLayout="column" fxLayoutGap="12px">
                          <div fxLayout="row" fxLayout.lt-md="column" fxLayoutGap="12px">
                            <mat-form-field fxFlex>
                              <mat-label>Primary Phone</mat-label>
                              <input matInput formControlName="firmPhone">
                              <mat-error *ngIf="showApplicationControlError('firmPhone', 'required')">Primary phone is required.</mat-error>
                              <mat-error *ngIf="showApplicationControlError('firmPhone', 'pattern')">Enter a valid phone number.</mat-error>
                            </mat-form-field>

                            <mat-form-field fxFlex>
                              <mat-label>Alternate Phone</mat-label>
                              <input matInput formControlName="firmAlternatePhone">
                              <mat-error *ngIf="showApplicationControlError('firmAlternatePhone', 'pattern')">Enter a valid phone number.</mat-error>
                            </mat-form-field>
                          </div>

                          <div fxLayout="row" fxLayout.lt-md="column" fxLayoutGap="12px">
                            <mat-form-field fxFlex>
                              <mat-label>Email</mat-label>
                              <input matInput formControlName="firmEmail">
                              <mat-error *ngIf="showApplicationControlError('firmEmail', 'required')">Email is required.</mat-error>
                              <mat-error *ngIf="showApplicationControlError('firmEmail', 'email')">Enter a valid email address.</mat-error>
                            </mat-form-field>

                            <mat-form-field fxFlex>
                              <mat-label>Confirm Email</mat-label>
                              <input matInput formControlName="firmConfirmEmail">
                              <mat-error *ngIf="showApplicationControlError('firmConfirmEmail', 'required')">Confirm email is required.</mat-error>
                              <mat-error *ngIf="showApplicationControlError('firmConfirmEmail', 'email')">Enter a valid email address.</mat-error>
                              <mat-error *ngIf="showApplicationControlError('firmConfirmEmail', 'emailMismatch')">Email addresses must match.</mat-error>
                            </mat-form-field>
                          </div>

                          <mat-form-field>
                            <mat-label>Website</mat-label>
                            <input matInput formControlName="firmWebsite">
                          </mat-form-field>

                          <div *ngIf="showLbpaFields" class="services-grid">
                            <mat-checkbox formControlName="inspections">Inspections</mat-checkbox>
                            <mat-checkbox formControlName="clearanceTesting">Clearance Testing</mat-checkbox>
                            <mat-checkbox formControlName="leadHazardScreening">Lead Hazard Screening</mat-checkbox>
                            <mat-checkbox formControlName="riskAssessments">Risk Assessments</mat-checkbox>
                            <mat-checkbox formControlName="abatement">Abatement</mat-checkbox>
                          </div>
                        </div>
                      </mat-card-content>
                    </mat-card>

                    <mat-card class="content-card workflow-card">
                      <mat-card-title>Contact Person</mat-card-title>
                      <mat-card-content>
                        <div fxLayout="column" fxLayoutGap="12px">
                          <div fxLayout="row" fxLayout.lt-md="column" fxLayoutGap="12px">
                            <mat-form-field fxFlex>
                              <mat-label>First Name</mat-label>
                              <input matInput formControlName="contactFirstName">
                              <mat-error *ngIf="showApplicationControlError('contactFirstName', 'required')">First name is required.</mat-error>
                            </mat-form-field>

                            <mat-form-field fxFlex>
                              <mat-label>Last Name</mat-label>
                              <input matInput formControlName="contactLastName">
                              <mat-error *ngIf="showApplicationControlError('contactLastName', 'required')">Last name is required.</mat-error>
                            </mat-form-field>
                          </div>

                          <div fxLayout="row" fxLayout.lt-md="column" fxLayoutGap="12px">
                            <mat-form-field fxFlex>
                              <mat-label>Title</mat-label>
                              <input matInput formControlName="contactTitle">
                            </mat-form-field>

                            <mat-form-field fxFlex>
                              <mat-label>Middle Name</mat-label>
                              <input matInput formControlName="contactMiddleName">
                            </mat-form-field>
                          </div>

                          <div fxLayout="row" fxLayout.lt-md="column" fxLayoutGap="12px">
                            <mat-form-field fxFlex>
                              <mat-label>Primary Phone</mat-label>
                              <input matInput formControlName="contactPhone">
                              <mat-error *ngIf="showApplicationControlError('contactPhone', 'required')">Primary phone is required.</mat-error>
                              <mat-error *ngIf="showApplicationControlError('contactPhone', 'pattern')">Enter a valid phone number.</mat-error>
                            </mat-form-field>

                            <mat-form-field fxFlex>
                              <mat-label>Alternate Phone</mat-label>
                              <input matInput formControlName="contactAlternatePhone">
                              <mat-error *ngIf="showApplicationControlError('contactAlternatePhone', 'pattern')">Enter a valid phone number.</mat-error>
                            </mat-form-field>
                          </div>

                          <div fxLayout="row" fxLayout.lt-md="column" fxLayoutGap="12px">
                            <mat-form-field fxFlex>
                              <mat-label>Email</mat-label>
                              <input matInput formControlName="contactEmail">
                              <mat-error *ngIf="showApplicationControlError('contactEmail', 'required')">Email is required.</mat-error>
                              <mat-error *ngIf="showApplicationControlError('contactEmail', 'email')">Enter a valid email address.</mat-error>
                            </mat-form-field>

                            <mat-form-field fxFlex>
                              <mat-label>Confirm Email</mat-label>
                              <input matInput formControlName="contactConfirmEmail">
                              <mat-error *ngIf="showApplicationControlError('contactConfirmEmail', 'required')">Confirm email is required.</mat-error>
                              <mat-error *ngIf="showApplicationControlError('contactConfirmEmail', 'email')">Enter a valid email address.</mat-error>
                              <mat-error *ngIf="showApplicationControlError('contactConfirmEmail', 'emailMismatch')">Email addresses must match.</mat-error>
                            </mat-form-field>
                          </div>
                        </div>
                      </mat-card-content>
                    </mat-card>
                  </div>
                </form>
              </div>
            </mat-step>

            <mat-step [hasError]="stepHasValidationErrors(2)">
              <ng-template matStepLabel>Certified Individuals</ng-template>

              <div class="scroll-container" [class.has-feedback]="hasFeedbackContent()">
                <div class="step-content" fxLayout="column" fxLayoutGap="16px">
                  <mat-card class="info-banner">
                    <div fxLayout="row" fxLayoutAlign="start start" fxLayoutGap="12px">
                      <mat-icon>info</mat-icon>
                      <span>Add only existing people already in the system. Person lookup starts after at least 3 characters and does not create new records from this workflow.</span>
                    </div>
                  </mat-card>

                  <mat-card class="content-card workflow-card">
                    <mat-card-title>Certified Individuals</mat-card-title>
                    <mat-card-content>
                      <div class="table-toolbar">
                        <div class="table-summary">Results ({{ filteredPeople.length }})</div>

                        <mat-form-field class="table-filter-field">
                          <mat-label>Filter Individuals</mat-label>
                          <input matInput [(ngModel)]="peopleFilterString" [ngModelOptions]="{ standalone: true }" (input)="filterPeopleTable()">
                        </mat-form-field>

                        <button mat-raised-button color="primary" type="button" *ngIf="canEdit" (click)="addPerson()" data-focus-id="add-existing-individual">
                          <mat-icon>person_search</mat-icon>
                          Add Existing Individual
                        </button>
                      </div>

                      <div *ngIf="!filteredPeople.length" class="empty-state small">No certified individuals added yet.</div>

                      <mat-table *ngIf="filteredPeople.length" [dataSource]="filteredPeople" class="mat-elevation-z8 step-table certified-individuals-table">
                        <ng-container matColumnDef="name">
                          <mat-header-cell *matHeaderCellDef>Name</mat-header-cell>
                          <mat-cell *matCellDef="let row">
                            <div *ngIf="sharedService.isMobile" fxLayout="row">
                              <span><strong>Name: </strong></span>&nbsp;
                              <span>{{ row.firstName }} {{ row.lastName }}</span>
                            </div>
                            <div *ngIf="!sharedService.isMobile">{{ row.firstName }} {{ row.lastName }}</div>
                          </mat-cell>
                        </ng-container>

                        <ng-container matColumnDef="primary">
                          <mat-header-cell *matHeaderCellDef>{{ showLbpaFields ? 'Discipline' : 'Training Provider' }}</mat-header-cell>
                          <mat-cell *matCellDef="let row">
                            <div *ngIf="sharedService.isMobile" fxLayout="row">
                              <span><strong>{{ showLbpaFields ? 'Discipline' : 'Training Provider' }}: </strong></span>&nbsp;
                              <span>{{ showLbpaFields ? (row.discipline || 'Not provided') : (row.atp || 'Not provided') }}</span>
                            </div>
                            <div *ngIf="!sharedService.isMobile">{{ showLbpaFields ? (row.discipline || 'Not provided') : (row.atp || 'Not provided') }}</div>
                          </mat-cell>
                        </ng-container>

                        <ng-container matColumnDef="secondary">
                          <mat-header-cell *matHeaderCellDef>{{ showLbpaFields ? 'Fee' : 'Training Date' }}</mat-header-cell>
                          <mat-cell *matCellDef="let row">
                            <div *ngIf="sharedService.isMobile" fxLayout="row">
                              <span><strong>{{ showLbpaFields ? 'Fee' : 'Training Date' }}: </strong></span>&nbsp;
                              <span *ngIf="showLbpaFields">{{ row.firmIsPaying ? 'Firm pays' : 'Self pays' }}</span>
                              <span *ngIf="!showLbpaFields">{{ row.trainingDate ? (row.trainingDate | date:'MM/dd/yyyy') : 'Not provided' }}</span>
                            </div>
                            <div *ngIf="!sharedService.isMobile">
                              <span *ngIf="showLbpaFields">{{ row.firmIsPaying ? 'Firm pays' : 'Self pays' }}</span>
                              <span *ngIf="!showLbpaFields">{{ row.trainingDate ? (row.trainingDate | date:'MM/dd/yyyy') : 'Not provided' }}</span>
                            </div>
                          </mat-cell>
                        </ng-container>

                        <ng-container matColumnDef="actions">
                          <mat-header-cell *matHeaderCellDef>Actions</mat-header-cell>
                          <mat-cell *matCellDef="let row">
                            <div class="action-buttons" *ngIf="sharedService.isMobile">
                              <span><strong>Actions: </strong></span>
                              <div>
                                <button mat-icon-button color="primary" type="button" (click)="editPerson(row); $event.stopPropagation()">
                                  <mat-icon>edit</mat-icon>
                                </button>
                                <button mat-icon-button color="warn" type="button" (click)="deletePerson(row); $event.stopPropagation()">
                                  <mat-icon>delete</mat-icon>
                                </button>
                              </div>
                            </div>
                            <div *ngIf="!sharedService.isMobile" class="action-buttons compact">
                              <button mat-icon-button color="primary" type="button" (click)="editPerson(row); $event.stopPropagation()">
                                <mat-icon>edit</mat-icon>
                              </button>
                              <button mat-icon-button color="warn" type="button" (click)="deletePerson(row); $event.stopPropagation()">
                                <mat-icon>delete</mat-icon>
                              </button>
                            </div>
                          </mat-cell>
                        </ng-container>

                        <mat-header-row fxLayout="row" [fxHide.lt-md]="true" fxLayout.lt-md="column" *matHeaderRowDef="certifiedIndividualTableColumns"></mat-header-row>
                        <mat-row fxLayout="row" fxLayout.lt-md="column" [ngClass]="sharedService.isMobile ? 'mobile-row' : ''" [ngStyle]="{ '--hover-bg-color': sharedService.hoverBackgroundColor }" *matRowDef="let row; columns: certifiedIndividualTableColumns"></mat-row>
                      </mat-table>
                    </mat-card-content>
                  </mat-card>
                </div>
              </div>
            </mat-step>

            <mat-step [hasError]="stepHasValidationErrors(3)">
              <ng-template matStepLabel>Documents</ng-template>

              <div class="scroll-container" [class.has-feedback]="hasFeedbackContent()">
                <div class="step-content" fxLayout="column" fxLayoutGap="16px">
                  <mat-card class="info-banner">
                    <div fxLayout="row" fxLayoutAlign="start start" fxLayoutGap="12px">
                      <mat-icon>info</mat-icon>
                      <span>Upload supporting documents for the application and, when needed, associate them with a certified individual already added to this application.</span>
                    </div>
                  </mat-card>

                  <mat-card class="content-card workflow-card">
                    <mat-card-title>Documents</mat-card-title>
                    <mat-card-content>
                      <div class="table-toolbar">
                        <div class="table-summary">Results ({{ filteredDocuments.length }})</div>

                        <mat-form-field class="table-filter-field">
                          <mat-label>Filter Documents</mat-label>
                          <input matInput [(ngModel)]="documentsFilterString" [ngModelOptions]="{ standalone: true }" (input)="filterDocumentTable()">
                        </mat-form-field>
                        <button mat-raised-button color="primary" type="button" *ngIf="canEdit" (click)="openDocumentUploadDialog()" data-focus-id="upload-document">
                          <mat-icon>upload_file</mat-icon>
                          Upload Document
                        </button>
                      </div>

                      <div *ngIf="!filteredDocuments.length" class="empty-state small">No documents uploaded yet.</div>

                      <mat-table *ngIf="filteredDocuments.length" [dataSource]="filteredDocuments" class="mat-elevation-z8 step-table">
                        <ng-container matColumnDef="fileName">
                          <mat-header-cell *matHeaderCellDef>File</mat-header-cell>
                          <mat-cell *matCellDef="let row">
                            <div *ngIf="sharedService.isMobile" fxLayout="row">
                              <span><strong>File: </strong></span>&nbsp;
                              <span>{{ row.fileName }}</span>
                            </div>
                            <div *ngIf="!sharedService.isMobile">{{ row.fileName }}</div>
                          </mat-cell>
                        </ng-container>

                        <ng-container matColumnDef="description">
                          <mat-header-cell *matHeaderCellDef>Description</mat-header-cell>
                          <mat-cell *matCellDef="let row">
                            <div *ngIf="sharedService.isMobile" fxLayout="row">
                              <span><strong>Description: </strong></span>&nbsp;
                              <span>{{ row.fileDescription || 'None' }}</span>
                            </div>
                            <div *ngIf="!sharedService.isMobile">{{ row.fileDescription || 'None' }}</div>
                          </mat-cell>
                        </ng-container>

                        <ng-container matColumnDef="association">
                          <mat-header-cell *matHeaderCellDef>Association</mat-header-cell>
                          <mat-cell *matCellDef="let row">
                            <div *ngIf="sharedService.isMobile" fxLayout="row">
                              <span><strong>Association: </strong></span>&nbsp;
                              <span>{{ getPersonLabel(row.applicationPersonId) }}</span>
                            </div>
                            <div *ngIf="!sharedService.isMobile">{{ getPersonLabel(row.applicationPersonId) }}</div>
                          </mat-cell>
                        </ng-container>

                        <ng-container matColumnDef="actions">
                          <mat-header-cell *matHeaderCellDef>Actions</mat-header-cell>
                          <mat-cell *matCellDef="let row">
                            <div class="action-buttons" *ngIf="sharedService.isMobile">
                              <span><strong>Actions: </strong></span>
                              <div>
                                <button mat-icon-button color="primary" type="button" (click)="downloadDocument(row); $event.stopPropagation()">
                                  <mat-icon>download</mat-icon>
                                </button>
                                <button mat-icon-button color="warn" type="button" *ngIf="canEdit" (click)="deleteDocument(row); $event.stopPropagation()">
                                  <mat-icon>delete</mat-icon>
                                </button>
                              </div>
                            </div>
                            <div *ngIf="!sharedService.isMobile" class="action-buttons compact">
                              <button mat-icon-button color="primary" type="button" (click)="downloadDocument(row); $event.stopPropagation()">
                                <mat-icon>download</mat-icon>
                              </button>
                              <button mat-icon-button color="warn" type="button" *ngIf="canEdit" (click)="deleteDocument(row); $event.stopPropagation()">
                                <mat-icon>delete</mat-icon>
                              </button>
                            </div>
                          </mat-cell>
                        </ng-container>

                        <mat-header-row fxLayout="row" [fxHide.lt-md]="true" fxLayout.lt-md="column" *matHeaderRowDef="documentTableColumns"></mat-header-row>
                        <mat-row fxLayout="row" fxLayout.lt-md="column" [ngClass]="sharedService.isMobile ? 'mobile-row' : ''" [ngStyle]="{ '--hover-bg-color': sharedService.hoverBackgroundColor }" *matRowDef="let row; columns: documentTableColumns"></mat-row>
                      </mat-table>
                    </mat-card-content>
                  </mat-card>
                </div>
              </div>
            </mat-step>

            <mat-step [hasError]="stepHasValidationErrors(4)">
              <ng-template matStepLabel>Review & Submit</ng-template>

              <div class="scroll-container" [class.has-feedback]="hasFeedbackContent()">
                <div class="step-content review-step-content" fxLayout="column" fxLayoutGap="16px">
                  <mat-card class="info-banner">
                    <div fxLayout="row" fxLayoutAlign="start start" fxLayoutGap="12px">
                      <mat-icon>fact_check</mat-icon>
                      <span>Review each section of your application below. All values shown here are read-only and reflect the current application model that will be submitted.</span>
                    </div>
                  </mat-card>

                  <mat-accordion class="review-accordion" multi>
                    <mat-expansion-panel class="review-panel mat-elevation-z2" [expanded]="true">
                      <mat-expansion-panel-header>
                        <mat-panel-title>Application Information</mat-panel-title>
                      </mat-expansion-panel-header>

                      <div class="review-grid">
                        <div class="review-field">
                          <div class="review-label">Application Type</div>
                          <div class="review-value">{{ applicationTypeLabel }}</div>
                        </div>

                        <div class="review-field">
                          <div class="review-label">Renewal</div>
                          <div class="review-value">{{ displayYesNo(applicationForm.get('renewal')?.value === true) }}</div>
                        </div>

                        <div class="review-field full-width">
                          <div class="review-label">Certificate to Renew</div>
                          <div class="review-value">{{ applicationForm.get('renewal')?.value ? displayReviewValue(applicationForm.get('previousApplication')?.value) : 'Not Applicable' }}</div>
                        </div>
                      </div>
                    </mat-expansion-panel>

                    <mat-expansion-panel class="review-panel mat-elevation-z2" [expanded]="true">
                      <mat-expansion-panel-header>
                        <mat-panel-title>Firm Information</mat-panel-title>
                      </mat-expansion-panel-header>

                      <div class="review-grid">
                        <div class="review-field">
                          <div class="review-label">Firm Name</div>
                          <div class="review-value">{{ displayReviewValue(applicationForm.get('firmName')?.value) }}</div>
                        </div>

                        <div class="review-field">
                          <div class="review-label">{{ firmIdentifierLabel }}</div>
                          <div class="review-value">{{ displayReviewValue(applicationForm.get('ccb')?.value) }}</div>
                        </div>

                        <ng-container *ngIf="showLbpaFields">
                          <div class="review-field">
                            <div class="review-label">Private Firm</div>
                            <div class="review-value">{{ displayYesNo(applicationForm.get('isPublic')?.value === true) }}</div>
                          </div>

                          <div class="review-field">
                            <div class="review-label">My Firm Provides Services For</div>
                            <div class="review-value">{{ displayReviewValue(applicationForm.get('firmServices')?.value) }}</div>
                          </div>

                          <div class="review-field full-width">
                            <div class="review-label">Firm Coverage</div>
                            <div class="review-value">{{ displayReviewValue(applicationForm.get('coverage')?.value) }}</div>
                          </div>

                          <div class="review-field full-width">
                            <div class="review-label">Lead-Based Paint Services</div>
                            <div class="review-value">
                              <div *ngIf="selectedFirmServiceLabels.length" class="review-list">
                                <span class="review-list-item" *ngFor="let service of selectedFirmServiceLabels">{{ service }}</span>
                              </div>
                              <span *ngIf="!selectedFirmServiceLabels.length">{{ reviewPlaceholder }}</span>
                            </div>
                          </div>
                        </ng-container>
                      </div>
                    </mat-expansion-panel>

                    <mat-expansion-panel class="review-panel mat-elevation-z2" [expanded]="true">
                      <mat-expansion-panel-header>
                        <mat-panel-title>Firm Address</mat-panel-title>
                      </mat-expansion-panel-header>

                      <div class="review-subsection-grid">
                        <div class="review-subsection-card">
                          <div class="review-subsection-title">Physical Address</div>
                          <div class="review-grid">
                            <div class="review-field full-width">
                              <div class="review-label">Street</div>
                              <div class="review-value">{{ displayReviewValue(applicationForm.get('firmPhysicalStreet')?.value) }}</div>
                            </div>

                            <div class="review-field">
                              <div class="review-label">City</div>
                              <div class="review-value">{{ displayReviewValue(applicationForm.get('firmPhysicalCity')?.value) }}</div>
                            </div>

                            <div class="review-field">
                              <div class="review-label">State</div>
                              <div class="review-value">{{ displayReviewValue(applicationForm.get('firmPhysicalState')?.value) }}</div>
                            </div>

                            <div class="review-field">
                              <div class="review-label">County</div>
                              <div class="review-value">{{ displayReviewValue(applicationForm.get('firmPhysicalCounty')?.value) }}</div>
                            </div>

                            <div class="review-field">
                              <div class="review-label">ZIP</div>
                              <div class="review-value">{{ displayReviewValue(applicationForm.get('firmPhysicalZip')?.value) }}</div>
                            </div>
                          </div>
                        </div>

                        <div class="review-subsection-card">
                          <div class="review-subsection-title">Mailing Address</div>

                          <div *ngIf="isMailingSameAsPhysical; else mailingAddressDetails" class="review-empty-state">
                            Mailing address is the same as the physical address.
                          </div>

                          <ng-template #mailingAddressDetails>
                            <div class="review-grid">
                              <div class="review-field full-width">
                                <div class="review-label">Street</div>
                                <div class="review-value">{{ displayReviewValue(applicationForm.get('firmMailingStreet')?.value) }}</div>
                              </div>

                              <div class="review-field">
                                <div class="review-label">City</div>
                                <div class="review-value">{{ displayReviewValue(applicationForm.get('firmMailingCity')?.value) }}</div>
                              </div>

                              <div class="review-field">
                                <div class="review-label">State</div>
                                <div class="review-value">{{ displayReviewValue(applicationForm.get('firmMailingState')?.value) }}</div>
                              </div>

                              <div class="review-field">
                                <div class="review-label">County</div>
                                <div class="review-value">{{ displayReviewValue(applicationForm.get('firmMailingCounty')?.value) }}</div>
                              </div>

                              <div class="review-field">
                                <div class="review-label">ZIP</div>
                                <div class="review-value">{{ displayReviewValue(applicationForm.get('firmMailingZip')?.value) }}</div>
                              </div>
                            </div>
                          </ng-template>
                        </div>
                      </div>
                    </mat-expansion-panel>

                    <mat-expansion-panel class="review-panel mat-elevation-z2" [expanded]="true">
                      <mat-expansion-panel-header>
                        <mat-panel-title>Firm Contact Information</mat-panel-title>
                      </mat-expansion-panel-header>

                      <div class="review-grid">
                        <div class="review-field">
                          <div class="review-label">Primary Phone</div>
                          <div class="review-value">{{ displayReviewValue(applicationForm.get('firmPhone')?.value) }}</div>
                        </div>

                        <div class="review-field">
                          <div class="review-label">Alternate Phone</div>
                          <div class="review-value">{{ displayReviewValue(applicationForm.get('firmAlternatePhone')?.value) }}</div>
                        </div>

                        <div class="review-field">
                          <div class="review-label">Fax</div>
                          <div class="review-value">{{ displayReviewValue(applicationForm.get('firmFaxPhone')?.value) }}</div>
                        </div>

                        <div class="review-field">
                          <div class="review-label">Email</div>
                          <div class="review-value">{{ displayReviewValue(applicationForm.get('firmEmail')?.value) }}</div>
                        </div>

                        <div class="review-field">
                          <div class="review-label">Confirm Email</div>
                          <div class="review-value">{{ displayReviewValue(applicationForm.get('firmConfirmEmail')?.value) }}</div>
                        </div>

                        <div class="review-field">
                          <div class="review-label">Website</div>
                          <div class="review-value">{{ displayReviewValue(applicationForm.get('firmWebsite')?.value) }}</div>
                        </div>
                      </div>
                    </mat-expansion-panel>

                    <mat-expansion-panel class="review-panel mat-elevation-z2" [expanded]="true">
                      <mat-expansion-panel-header>
                        <mat-panel-title>Contact Person</mat-panel-title>
                      </mat-expansion-panel-header>

                      <div class="review-grid">
                        <div class="review-field full-width">
                          <div class="review-label">Selected Contact Person</div>
                          <div class="review-value">{{ getFullName(applicationForm.get('contactFirstName')?.value, applicationForm.get('contactMiddleName')?.value, applicationForm.get('contactLastName')?.value) }}</div>
                        </div>

                        <div class="review-field">
                          <div class="review-label">Title</div>
                          <div class="review-value">{{ displayReviewValue(applicationForm.get('contactTitle')?.value) }}</div>
                        </div>

                        <div class="review-field">
                          <div class="review-label">First Name</div>
                          <div class="review-value">{{ displayReviewValue(applicationForm.get('contactFirstName')?.value) }}</div>
                        </div>

                        <div class="review-field">
                          <div class="review-label">Middle Name</div>
                          <div class="review-value">{{ displayReviewValue(applicationForm.get('contactMiddleName')?.value) }}</div>
                        </div>

                        <div class="review-field">
                          <div class="review-label">Last Name</div>
                          <div class="review-value">{{ displayReviewValue(applicationForm.get('contactLastName')?.value) }}</div>
                        </div>

                        <div class="review-field">
                          <div class="review-label">Primary Phone</div>
                          <div class="review-value">{{ displayReviewValue(applicationForm.get('contactPhone')?.value) }}</div>
                        </div>

                        <div class="review-field">
                          <div class="review-label">Alternate Phone</div>
                          <div class="review-value">{{ displayReviewValue(applicationForm.get('contactAlternatePhone')?.value) }}</div>
                        </div>

                        <div class="review-field">
                          <div class="review-label">Fax</div>
                          <div class="review-value">{{ displayReviewValue(applicationForm.get('contactFaxPhone')?.value) }}</div>
                        </div>

                        <div class="review-field">
                          <div class="review-label">Email</div>
                          <div class="review-value">{{ displayReviewValue(applicationForm.get('contactEmail')?.value) }}</div>
                        </div>

                        <div class="review-field">
                          <div class="review-label">Confirm Email</div>
                          <div class="review-value">{{ displayReviewValue(applicationForm.get('contactConfirmEmail')?.value) }}</div>
                        </div>
                      </div>
                    </mat-expansion-panel>

                    <mat-expansion-panel class="review-panel mat-elevation-z2" [expanded]="true">
                      <mat-expansion-panel-header>
                        <mat-panel-title>Certified Individuals</mat-panel-title>
                      </mat-expansion-panel-header>

                      <div *ngIf="!people.length" class="review-empty-state">{{ reviewPlaceholder }}</div>

                      <mat-table *ngIf="people.length" [dataSource]="people" class="mat-elevation-z8 step-table review-table certified-individuals-table">
                        <ng-container matColumnDef="name">
                          <mat-header-cell *matHeaderCellDef>Name</mat-header-cell>
                          <mat-cell *matCellDef="let row">
                            <div *ngIf="sharedService.isMobile" fxLayout="row">
                              <span><strong>Name: </strong></span>&nbsp;
                              <span>{{ getFullName(row.firstName, '', row.lastName) }}</span>
                            </div>
                            <div *ngIf="!sharedService.isMobile">{{ getFullName(row.firstName, '', row.lastName) }}</div>
                          </mat-cell>
                        </ng-container>

                        <ng-container matColumnDef="primary">
                          <mat-header-cell *matHeaderCellDef>{{ certifiedIndividualPrimaryLabel }}</mat-header-cell>
                          <mat-cell *matCellDef="let row">
                            <div *ngIf="sharedService.isMobile" fxLayout="row">
                              <span><strong>{{ certifiedIndividualPrimaryLabel }}: </strong></span>&nbsp;
                              <span>{{ showLbpaFields ? displayReviewValue(row.discipline) : displayReviewValue(row.atp) }}</span>
                            </div>
                            <div *ngIf="!sharedService.isMobile">{{ showLbpaFields ? displayReviewValue(row.discipline) : displayReviewValue(row.atp) }}</div>
                          </mat-cell>
                        </ng-container>

                        <ng-container matColumnDef="secondary">
                          <mat-header-cell *matHeaderCellDef>{{ certifiedIndividualSecondaryLabel }}</mat-header-cell>
                          <mat-cell *matCellDef="let row">
                            <div *ngIf="sharedService.isMobile" fxLayout="row">
                              <span><strong>{{ certifiedIndividualSecondaryLabel }}: </strong></span>&nbsp;
                              <span *ngIf="showLbpaFields">{{ row.firmIsPaying ? 'Firm Pays' : 'Self Pays' }}</span>
                              <span *ngIf="!showLbpaFields">{{ row.trainingDate ? (row.trainingDate | date:'MM/dd/yyyy') : reviewPlaceholder }}</span>
                            </div>
                            <div *ngIf="!sharedService.isMobile">
                              <span *ngIf="showLbpaFields">{{ row.firmIsPaying ? 'Firm Pays' : 'Self Pays' }}</span>
                              <span *ngIf="!showLbpaFields">{{ row.trainingDate ? (row.trainingDate | date:'MM/dd/yyyy') : reviewPlaceholder }}</span>
                            </div>
                          </mat-cell>
                        </ng-container>

                        <mat-header-row fxLayout="row" [fxHide.lt-md]="true" fxLayout.lt-md="column" *matHeaderRowDef="reviewCertifiedIndividualTableColumns"></mat-header-row>
                        <mat-row fxLayout="row" fxLayout.lt-md="column" [ngClass]="sharedService.isMobile ? 'mobile-row' : ''" [ngStyle]="{ '--hover-bg-color': sharedService.hoverBackgroundColor }" *matRowDef="let row; columns: reviewCertifiedIndividualTableColumns"></mat-row>
                      </mat-table>
                    </mat-expansion-panel>

                    <mat-expansion-panel class="review-panel mat-elevation-z2" [expanded]="true">
                      <mat-expansion-panel-header>
                        <mat-panel-title>Documents</mat-panel-title>
                      </mat-expansion-panel-header>

                      <div *ngIf="!documents.length" class="review-empty-state">{{ reviewPlaceholder }}</div>

                      <mat-table *ngIf="documents.length" [dataSource]="documents" class="mat-elevation-z8 step-table review-table">
                        <ng-container matColumnDef="type">
                          <mat-header-cell *matHeaderCellDef>Document Type</mat-header-cell>
                          <mat-cell *matCellDef="let row">
                            <div *ngIf="sharedService.isMobile" fxLayout="row">
                              <span><strong>Document Type: </strong></span>&nbsp;
                              <span>{{ getDocumentTypeLabel(row) }}</span>
                            </div>
                            <div *ngIf="!sharedService.isMobile">{{ getDocumentTypeLabel(row) }}</div>
                          </mat-cell>
                        </ng-container>

                        <ng-container matColumnDef="fileName">
                          <mat-header-cell *matHeaderCellDef>File Name</mat-header-cell>
                          <mat-cell *matCellDef="let row">
                            <div *ngIf="sharedService.isMobile" fxLayout="row">
                              <span><strong>File Name: </strong></span>&nbsp;
                              <span>{{ displayReviewValue(row.fileName) }}</span>
                            </div>
                            <div *ngIf="!sharedService.isMobile">{{ displayReviewValue(row.fileName) }}</div>
                          </mat-cell>
                        </ng-container>

                        <ng-container matColumnDef="description">
                          <mat-header-cell *matHeaderCellDef>Description</mat-header-cell>
                          <mat-cell *matCellDef="let row">
                            <div *ngIf="sharedService.isMobile" fxLayout="row">
                              <span><strong>Description: </strong></span>&nbsp;
                              <span>{{ displayReviewValue(row.fileDescription) }}</span>
                            </div>
                            <div *ngIf="!sharedService.isMobile">{{ displayReviewValue(row.fileDescription) }}</div>
                          </mat-cell>
                        </ng-container>

                        <ng-container matColumnDef="association">
                          <mat-header-cell *matHeaderCellDef>Association</mat-header-cell>
                          <mat-cell *matCellDef="let row">
                            <div *ngIf="sharedService.isMobile" fxLayout="row">
                              <span><strong>Association: </strong></span>&nbsp;
                              <span>{{ getPersonLabel(row.applicationPersonId) }}</span>
                            </div>
                            <div *ngIf="!sharedService.isMobile">{{ getPersonLabel(row.applicationPersonId) }}</div>
                          </mat-cell>
                        </ng-container>

                        <ng-container matColumnDef="status">
                          <mat-header-cell *matHeaderCellDef>Status</mat-header-cell>
                          <mat-cell *matCellDef="let row">
                            <div *ngIf="sharedService.isMobile" fxLayout="row">
                              <span><strong>Status: </strong></span>&nbsp;
                              <span>{{ getDocumentStatusLabel(row) }}</span>
                            </div>
                            <div *ngIf="!sharedService.isMobile">{{ getDocumentStatusLabel(row) }}</div>
                          </mat-cell>
                        </ng-container>

                        <mat-header-row fxLayout="row" [fxHide.lt-md]="true" fxLayout.lt-md="column" *matHeaderRowDef="reviewDocumentTableColumns"></mat-header-row>
                        <mat-row fxLayout="row" fxLayout.lt-md="column" [ngClass]="sharedService.isMobile ? 'mobile-row' : ''" [ngStyle]="{ '--hover-bg-color': sharedService.hoverBackgroundColor }" *matRowDef="let row; columns: reviewDocumentTableColumns"></mat-row>
                      </mat-table>
                    </mat-expansion-panel>
                  </mat-accordion>

                  <form [formGroup]="attestationForm">
                    <mat-card class="content-card">
                      <mat-card-title>Signature and Acknowledgments</mat-card-title>
                      <mat-card-content>
                        <div fxLayout="column" fxLayoutGap="12px">
                          <mat-checkbox formControlName="attestation">I understand and agree with the information provided in this application.</mat-checkbox>
                          <mat-error *ngIf="showAttestationError('attestation', 'requiredTrue')">You must attest before submitting.</mat-error>

                          <mat-form-field>
                            <mat-label>Name or Initials</mat-label>
                            <input matInput formControlName="esignature">
                            <mat-error *ngIf="showAttestationError('esignature', 'required')">Electronic signature is required.</mat-error>
                          </mat-form-field>
                        </div>
                      </mat-card-content>
                    </mat-card>
                  </form>
                </div>
              </div>
            </mat-step>
          </ng-container>

        <ng-template #linkWorkflow>
          <mat-step [hasError]="stepHasValidationErrors(0)">
            <ng-template matStepLabel>Firm Information</ng-template>

            <div class="scroll-container" [class.has-feedback]="hasFeedbackContent()">
              <div class="step-content" fxLayout="column" fxLayoutGap="16px">
                <mat-card class="info-banner">
                  <div fxLayout="row" fxLayoutAlign="start start" fxLayoutGap="12px">
                    <mat-icon class="link-step-icon">info</mat-icon>
                    <span class="link-step-banner-text">Please enter the existing Firm name below then click the next button.</span>
                  </div>
                </mat-card>

                <form [formGroup]="applicationForm" class="link-step-form">
                  <mat-form-field appearance="fill" class="full-width-field">
                    <mat-label>Firm Name</mat-label>
                    <input matInput formControlName="firmName" [matAutocomplete]="firmNameAutocomplete" (input)="searchFirmNames($any($event.target).value)">
                    <mat-autocomplete #firmNameAutocomplete="matAutocomplete">
                      <mat-option *ngFor="let firm of linkSearchResults" [value]="firm.name">{{ firm.name }}</mat-option>
                    </mat-autocomplete>
                    <mat-error *ngIf="showApplicationControlError('firmName', 'required')">Firm name is required.</mat-error>
                    <mat-error *ngIf="showApplicationControlError('firmName', 'linkNotFound')">{{ linkLookupMessage }}</mat-error>
                  </mat-form-field>
                </form>

                <div class="link-lookup-status" *ngIf="linkLookupLoading">
                  <mat-spinner diameter="28" strokeWidth="4"></mat-spinner>
                  <span>Searching for a matching public Firm...</span>
                </div>

                <div class="link-lookup-status success" *ngIf="!linkLookupLoading && linkSummary">
                  <mat-icon>check_circle</mat-icon>
                  <span>Matched public Firm: <strong>{{ linkSummary.firm.name }}</strong></span>
                </div>

                <mat-card class="warning-banner" *ngIf="linkLookupFailed && linkLookupMessage">
                  <div fxLayout="row" fxLayoutAlign="start start" fxLayoutGap="12px">
                    <mat-icon>warning</mat-icon>
                    <span>{{ linkLookupMessage }}</span>
                  </div>
                </mat-card>
              </div>
            </div>
          </mat-step>

          <mat-step [hasError]="stepHasValidationErrors(1)">
            <ng-template matStepLabel>Signature and Acknowledgments</ng-template>

            <div class="scroll-container" [class.has-feedback]="hasFeedbackContent()">
              <div style="padding: 16px;">
                <div *ngIf="linkLookupLoading" style="display: flex; align-items: center; justify-content: center; min-height: 100px;">
                  <mat-spinner diameter="48" strokeWidth="6" style="border-radius: 50%; margin-right: 16px;"></mat-spinner>
                  <span style="font-size: 1.2rem;">Searching...</span>
                </div>

                <div *ngIf="!linkLookupLoading && linkSummary" fxLayout="column" fxLayoutGap="16px">
                  <mat-card class="warning-banner">
                    <div fxLayout="row" fxLayoutAlign="start start" fxLayoutGap="12px">
                      <mat-icon style="color: #e65100; flex-shrink: 0;">info</mat-icon>
                      <span style="color: #e65100; font-size: 14px; line-height: 1.5;">
                        Please verify that the information below is correct
                      </span>
                    </div>
                  </mat-card>

                  <p>Firm: <strong>{{ linkSummary.firm.name }}</strong></p>
                  <ng-container *ngIf="hasLinkApprovalContactEmails; else firmLinkFallbackNotice">
                    <p *ngIf="!hasMultipleLinkApprovalContactEmails">
                      An approval email will be sent to the contact email on file:
                      <a [href]="'mailto:' + linkApprovalContactEmails[0]" style="color: #1976d2;"><strong>{{ linkApprovalContactEmails[0] }}</strong></a>
                    </p>
                    <div *ngIf="hasMultipleLinkApprovalContactEmails">
                      <p>Approval emails will be sent to the contact emails on file:</p>
                      <ul style="margin-top: 0;">
                        <li *ngFor="let approvalEmail of linkApprovalContactEmails">
                          <a [href]="'mailto:' + approvalEmail" style="color: #1976d2;"><strong>{{ approvalEmail }}</strong></a>
                        </li>
                      </ul>
                    </div>
                  </ng-container>
                  <ng-template #firmLinkFallbackNotice>
                    <p>
                      No valid contact email was found for this Firm. This request will be sent to
                      <a href="mailto:leadprogram@odhsoha.oregon.gov" style="color: #1976d2;"><strong>leadprogram@odhsoha.oregon.gov</strong></a>
                      for staff review.
                    </p>
                  </ng-template>

                  <form [formGroup]="attestationForm">
                    <div fxLayout="column" fxLayoutGap="12px">
                      <mat-checkbox formControlName="attestation" required>
                        I attest that I am affiliated with the Firm listed above.
                      </mat-checkbox>
                      <div class="field-error-text" *ngIf="showAttestationError('attestation', 'requiredTrue')">You must attest before submitting.</div>

                      <mat-form-field appearance="fill">
                        <mat-label>E-Signature</mat-label>
                        <input matInput formControlName="esignature">
                        <mat-error *ngIf="showAttestationError('esignature', 'required')">E-Signature is required.</mat-error>
                      </mat-form-field>
                    </div>
                  </form>
                </div>

                <div *ngIf="!linkLookupLoading && !linkSummary" fxLayout="column" fxLayoutGap="16px">
                  <mat-card class="mat-elevation-2" style="background-color: #ffebee; border-left: 4px solid #f44336; padding: 16px;">
                    <div fxLayout="column" fxLayoutGap="12px">
                      <div fxLayout="row" fxLayoutAlign="start center" fxLayoutGap="8px">
                        <mat-icon color="warn">error_outline</mat-icon>
                        <h4 style="margin: 0; color: #d32f2f;">No Matching Firm Found</h4>
                      </div>
                      <p style="margin: 0; color: #666;">Please check the spelling and try again.</p>
                      <p style="margin: 0; color: #666;">
                        If you believe you are encountering this message in error, please contact us at
                        <a href="mailto:leadprogram@odhsoha.oregon.gov" style="color: #1976d2;">leadprogram@odhsoha.oregon.gov</a>
                        or <a href="tel:971-673-0440" style="color: #1976d2;">971-673-0440</a>.
                      </p>
                    </div>
                  </mat-card>
                </div>
              </div>
            </div>
          </mat-step>

          <mat-step *ngIf="isSubmitted">
            <ng-template matStepLabel>Confirmation</ng-template>

            <div class="scroll-container" [class.has-feedback]="hasFeedbackContent()">
              <div fxLayout="column" fxLayoutAlign="center center" style="padding: 40px; text-align: center;">
                <mat-card style="padding: 30px; max-width: 600px;">
                  <mat-card-content style="display: flex; flex-direction: column; align-items: center;">
                    <mat-icon style="font-size: 64px; height: 64px; width: 64px; color: #4caf50; margin-bottom: 20px;">check_circle</mat-icon>
                    <h2 style="color: #4caf50; margin-bottom: 20px;">Link Request Submitted Successfully!</h2>
                    <p style="font-size: 16px; line-height: 1.6; margin-bottom: 20px;">
                      Your link request has been submitted and is being reviewed.
                    </p>
                    <div style="padding: 16px; border-radius: 4px; margin-bottom: 20px; width: 100%;" *ngIf="hasLinkApprovalContactEmails; else firmConfirmationFallback">
                      <p style="margin: 4px 0;" *ngIf="!hasMultipleLinkApprovalContactEmails">
                        An approval email has been sent to:
                        <a [href]="'mailto:' + linkApprovalContactEmails[0]" style="color: #1976d2;"><strong>{{ linkApprovalContactEmails[0] }}</strong></a>
                      </p>
                      <div *ngIf="hasMultipleLinkApprovalContactEmails">
                        <p style="margin: 4px 0;">Approval emails have been sent to:</p>
                        <ul style="margin: 4px 0 0; text-align: left; display: inline-block;">
                          <li *ngFor="let approvalEmail of linkApprovalContactEmails">
                            <a [href]="'mailto:' + approvalEmail" style="color: #1976d2;"><strong>{{ approvalEmail }}</strong></a>
                          </li>
                        </ul>
                      </div>
                    </div>
                    <ng-template #firmConfirmationFallback>
                      <p style="font-size: 16px; line-height: 1.6; margin-bottom: 20px;">
                        No valid contact email was found for this Firm. The request has been sent to
                        <a href="mailto:leadprogram@odhsoha.oregon.gov" style="color: #1976d2;"><strong>leadprogram@odhsoha.oregon.gov</strong></a>
                        for staff review.
                      </p>
                    </ng-template>
                    <p style="font-size: 16px; line-height: 1.6; margin-bottom: 20px;">
                      If you have any questions or concerns, please contact us at:
                    </p>
                    <div style="padding: 16px; border-radius: 4px; margin-bottom: 20px;">
                      <p style="margin: 4px 0;"><strong>Email: </strong>
                        <a href="mailto:leadprogram@odhsoha.oregon.gov" style="color: #1976d2;">leadprogram@odhsoha.oregon.gov</a>
                      </p>
                      <p style="margin: 4px 0;"><strong>Phone: </strong>
                        <a href="tel:971-673-0440" style="color: #1976d2;">971-673-0440</a>
                      </p>
                    </div>
                  </mat-card-content>
                </mat-card>
              </div>
            </div>
          </mat-step>
        </ng-template>
      </mat-stepper>
      </ng-template>

      <div *ngIf="hasFeedbackContent()" class="validation-summary" [class.collapsed]="isFeedbackCollapsed">
        <button
          type="button"
          class="validation-summary-header validation-summary-toggle"
          (click)="toggleFeedbackPanel()"
          [attr.aria-expanded]="!isFeedbackCollapsed"
          [attr.aria-label]="isFeedbackCollapsed ? 'Expand deficiencies panel' : 'Minimize deficiencies panel'">
          <span class="validation-summary-title">
            <mat-icon>warning</mat-icon>
            <span>Application Feedback / Deficiencies</span>
          </span>
          <span class="validation-summary-action">{{ isFeedbackCollapsed ? 'Expand' : 'Minimize' }}</span>
        </button>
        <div
          *ngIf="!isFeedbackCollapsed"
          #validationSummaryCard
          class="validation-card">
          <ng-container *ngIf="getPersistedDeficiencyDescriptions() as persistedDeficiencies">
            <div class="validation-feedback-section" *ngIf="persistedDeficiencies.length > 0">
              <div class="feedback-text" *ngFor="let deficiencyDescription of persistedDeficiencies">
                {{ deficiencyDescription }}
              </div>
            </div>
          </ng-container>

          <ng-container *ngIf="getValidationFeedbackGroups() as validationGroups">
            <div class="validation-feedback-section" *ngIf="validationGroups.length > 0">
              <div class="validation-feedback-heading">Missing Required Fields</div>
              <div class="validation-feedback-group" *ngFor="let validationGroup of validationGroups">
                <div class="validation-feedback-step">{{ validationGroup.step }}</div>
                <div class="validation-feedback-links">
                  <button
                    type="button"
                    class="validation-feedback-link"
                    *ngFor="let validationError of validationGroup.errors"
                    (click)="focusValidationFeedbackError(validationError)">
                    {{ validationError.label }}
                  </button>
                </div>
              </div>
            </div>
          </ng-container>
        </div>
      </div>
      </div>
    </div>

    <mat-card class="action-card" [style.background-color]="uiData.firm.color" fxLayoutGap="8px">
      <button mat-raised-button type="button" (click)="close()" [disabled]="saving">{{ canEdit ? 'Close' : 'Exit' }}</button>
      <button mat-raised-button type="button" color="warn" *ngIf="showCancelButton()" (click)="cancelApplication()" [disabled]="isCancelButtonDisabled()">{{ getCancelButtonText() }}</button>
      <button mat-raised-button type="button" color="primary" *ngIf="!showWorkflowChooser && !isLinkWorkflow" (click)="saveDraft()" [disabled]="!canEdit || saving">Save</button>
      <div fxFlex="1 1 auto"></div>
      <button *ngIf="showPreviousButton()" mat-raised-button type="button" (click)="goToPreviousStep()" [disabled]="saving">Previous</button>
      <button *ngIf="showNextButton()" mat-raised-button type="button" color="primary" (click)="goToNextStep()" [disabled]="saving">Next</button>
      <button *ngIf="showSubmitButton()" mat-raised-button type="button" color="primary" (click)="submitApplication()" [disabled]="isSubmitButtonDisabled()">{{ isLinkWorkflow ? 'Submit Link Request' : 'Submit Application' }}</button>
    </mat-card>
  </ng-container>
</div>